import React from "react";
import { PropsWithChildren } from "react";
import { useDebounce } from "use-debounce";
import styles from "../styles/TemplateTemp.module.css";
import Styles from "../styles/Template.module.css";
import { Content } from "../components";
import ListHeader from "../components/ListHeader/ListHeader";
import {
  CustomTableRow,
  Table,
  TableBody,
  TableButtons,
  TableHead,
  TableNoContent,
  TableRowTitle,
  TableStatusWithToggle,
} from "../components/NewTable";
import ToggleModal from "../pageComponents/RtmTemplate/ToggleModal";
import TableSkeleton from "../Skeletons/TableSkeleton";
import Pagination from "../components/NewTable/Pagination";
import {
  TemplateListHeadData,
} from "../hooks/rtmTemplate/staticData";
import AddTemplateModal from "../pageComponents/RtmTemplate/AddTemplateModal";

import {
  useAllTemplate,
  useFetchType,
  useToggleStatus,
  useEditTemplate,
} from "../hooks/rtmTemplate";
import { IEditTemplateRes, IRtmTemplate } from "../hooks/rtmTemplate/index.type";
import ViewQuestionModal, {
  AddEditModalProps,
} from "../pageComponents/RtmTemplate/ViewQuestionModal";
import { useFetchStatus } from "../hooks/patients";

export type DeleteModalState = PropsWithChildren<{
  status: boolean;
  data?: IRtmTemplate;
}>;

const RTMTemplate = () => {
  const [toggleModal, setToggleModal] = React.useState<DeleteModalState>({
    status: false,
  });
  const [templateId, setTemplateId] = React.useState("");
  const[EditTemplateData,setEditTemplateData] = React.useState<IEditTemplateRes>();
  const [toggleQuestionModal, setToggleQuestionModal] =
    React.useState<AddEditModalProps>({
      show: false,
      templateData: {
        _id: "",
        type: "",
        status: "",
        questionGroupedByCategory: "",
        name: "",
        resources: [],
        category: [],
        activeCategoryCount: 0,
        activeQuestionCount: 0,
      },
    });
  const [searchKey, setSearchKey] = React.useState<string>("");
  const [debouncedSearchKey] = useDebounce(searchKey, 300);
  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const [filters, setFilters] = React.useState<Record<string, string>>({});
  const { fetchStatus, status } = useFetchStatus();
  const { fetchTemplateType, templateType, fetchingTemplateType } =
    useFetchType();
  const [isAddTemplateModalVisible, setAddTemplateModalVisible] =
    React.useState<boolean>(false);
    const [sortConfig, setSortConfig] = React.useState<{
      key: string;
      direction: "asc" | "desc";
    }>({
      key: "",
      direction: "asc",
    });
  const { templateData, templateDataLoading, getTemplateData } = useAllTemplate(
    {
      searchKey: searchKey || "",
      page: pageNumber || 1,
      status: filters.status || "",
      type: filters.type || "",
      sort: sortConfig.key,
      order: sortConfig.direction,
    }
  );

  const {  getTemplateToEdit } =
    useEditTemplate();
  const { toggleTemplateStatus } = useToggleStatus();

  const perpageData = 8;

  React.useEffect(() => {
    getTemplateData();
  }, [filters, debouncedSearchKey, pageNumber, templateData?.totalResults,sortConfig]);

  React.useEffect(() => {
    fetchStatus();
    fetchTemplateType();
  }, []);


 

const fetchTemplateData = async (id: string) => {
  try {
    const data = await getTemplateToEdit({ templateId: id });
    return data;
  } catch (error) {
    console.error("Error fetching template data:", error);
    return null;
  }
};


const handleEdit = async (id: string) => {
  setAddTemplateModalVisible(true);
  setTemplateId(id);

  const data = await fetchTemplateData(id);
  if (data) {
    setEditTemplateData(data);
  }
};



  let filter = [
    {
      id: "1",
      disable: false,
      label: "Type",
      value: "type",
      options: [
        { label: "Template Type", value: "" },
        ...(templateType || [])?.map((type) => ({
          label: type.title,
          value: type.key,
        })),
      ],
    },
    {
      id: "2",
      disable: false,
      label: "Status",
      value: "status",
      options: [
        { label: "Status", value: "" },
        ...(status || []).map((status) => ({
          label: status.value,
          value: status.key,
        })),
      ],
    },
  ];

  const handleToggleAddTemplateModal = () => {
    setTemplateId('');
    setEditTemplateData(undefined)
    setAddTemplateModalVisible(!isAddTemplateModalVisible);
 
  };

  const handleToggleModal = () => {
    setToggleModal({ status: false });
  };

  const handleFiltersUpdate = (newFilters: Record<string, string>) => {
    setFilters(newFilters);
  };

  const handleSearch = (searchTerm: string) => {
    setSearchKey(searchTerm);
  };

  // ====================================== search function end ======================================

  const onPageChange = (currentPage: { selected: number }) => {
    setPageNumber(currentPage.selected + 1);
  };

  const handleToggleTemplateStatus = async (template: IRtmTemplate) => {
    const updatedStatus = template.status === "active" ? "inactive" : "active";

    try {
      const data = await toggleTemplateStatus({
        templateId: template._id,
        status: updatedStatus,
      });

      if (data?.status === 200) {
        getTemplateData();
      }
    } catch (error) {
      return;
    }
  };

  const handleEditTemplate = () => {
    setTemplateId("");
  };


     const sortedTemplate = React.useMemo(() => {
      if (!templateData?.data?.data) return [];
      return [...templateData.data.data];
    }, [templateData]);


    const handleSort = (key: string) => {
      setSortConfig((prev) => {
        if (prev.key === key) {
          return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
        }
        return { key, direction: "asc" };
      });
      setPageNumber(1); 
    };
  


  return (
    <Content
      currentMenu="rtm-template"
      list={true}
      contentClass={styles.contentContainer}
      customContainerStyle={{ height: "93.5rem" }}
      customContentStyle={{ height: "98rem" }}
    >
      <ListHeader
        title={isAddTemplateModalVisible ? "Add Template" : "Template"}
        actionType={isAddTemplateModalVisible ? "back" : "add"}
        actionButtonText={
          isAddTemplateModalVisible ? "Back to Template" : "Add Template"
        }
        searchPlaceholder="Search template by name"
        showFilter={!isAddTemplateModalVisible}
        onActionButtonClick={handleToggleAddTemplateModal}
        filterOptions={filter}
        setFilters={handleFiltersUpdate}
        onSearchChange={handleSearch}
        filterSearchClass={Styles.searchField}
      />

      {!isAddTemplateModalVisible && (
        <>
          <Table className={styles.table} style={{ height: "60rem" }}>
            <TableHead
              rowStyle={{ borderBottom: "none" }}
              data={TemplateListHeadData}
              sortKey={sortConfig?.key || ""}
              sortOrder={sortConfig?.direction || "asc"}
              onSort={handleSort}
            />
            <TableBody>
              <CustomTableRow>
                {templateDataLoading ? (
                  <TableSkeleton
                    rows={TemplateListHeadData.length}
                    columns={10}
                  />
                ) : templateData && sortedTemplate?.length > 0 ? (
                  sortedTemplate?.map((template) => (
                    <tr key={template._id} style={{ padding: "0.5rem 0.5rem" }}>
                      <td style={{ padding: "1rem 0.5rem" }}></td>
                      <TableRowTitle style={{ fontSize: "1.6rem" }}>
                        {template.name}
                      </TableRowTitle>
                      <td>
                        {template.type
                          ?.replace(/([a-z])([A-Z])/g, "$1 $2")
                          .replace(/([A-Z])/g, " $1")
                          .split(" ")
                          .filter(Boolean)
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() +
                              word.slice(1).toLowerCase()
                          )
                          .join(" ")}
                      </td>
                      <td></td>

                      {/* Category Count Column */}
                      <td
                        style={{
                          display: "flex",
                          alignItems: "center",
                          height: "100%",
                        }}
                      >
                        <p style={{ margin: "1rem", height: "100%" }}>
                          {template.activeCategoryCount}
                        </p>
                        {template.activeCategoryCount >= 1 && (
                          <TableButtons
                            customStyle={{
                              border: "none",
                              alignItems: "center",
                              paddingTop: "1.6rem",
                              height: "100%",
                            }}
                            buttons={[
                              {
                                icon: "new-eye-icon",
                                tooltip: "View Category",
                                onClick: () =>
                                  setToggleModal({
                                    status: true,
                                    data: template,
                                  }),
                              },
                            ]}
                          />
                        )}
                      </td>
                      <td></td>
                      {/* Question Count Column */}
                      <td
                        style={{
                          display: "flex",
                          alignItems: "center",
                          height: "100%",
                        }}
                      >
                        <p style={{ margin: "1rem", height: "100%" }}>
                          {template.activeQuestionCount}
                        </p>
                        {template.activeQuestionCount >= 1 && (
                          <TableButtons
                            customStyle={{
                              border: "none",
                              // alignItems: "center",
                              paddingTop: "1.6rem",
                              height: "100%",
                            }}
                            buttons={[
                              {
                                icon: "new-eye-icon",
                                tooltip: "View Question",
                                onClick: () =>
                                  setToggleQuestionModal({
                                    show: true,
                                    templateData: template,
                                  }),
                              },
                            ]}
                          />
                        )}
                      </td>

                      {/* Status Toggle */}
                      <td>
                        <TableStatusWithToggle
                          id={template._id}
                          status={
                            template.status === "active" ? "Active" : "Inactive"
                          }
                          onChange={() => handleToggleTemplateStatus(template)}
                          checked={template.status === "active"}
                        />
                      </td>

                      {/* Action Buttons */}
                      <TableButtons
                        tableButtonStyle={{ paddingLeft: "1.6rem" }}
                        buttons={[
                          {
                            icon: "update",
                            tooltip: "Edit Template",
                            onClick: () => {
                            handleEdit(template._id)
                            },
                          },
                        ]}
                      />
                    </tr>
                  ))
                ) : (
                  <TableNoContent
                    colSpan={TemplateListHeadData?.length}
                    message={
                      searchKey
                        ? "No Template found matching your search"
                        : "No Template found"
                    }
                  />
                )}
              </CustomTableRow>
            </TableBody>
          </Table>

          <Pagination
            itemsPerPage={perpageData}
            totalItems={templateData ? templateData.totalResults : 0}
            onPageChange={onPageChange}
          />
          {toggleModal.status && (
            <ToggleModal
              loading={templateDataLoading}
              toggleModal={toggleModal}
              onCloseModal={() => setToggleModal({ status: false })}
              handleToggle={handleToggleModal}
            />
          )}

          {toggleQuestionModal.show && (
            <ViewQuestionModal
              show={toggleQuestionModal.show}
              onClose={() =>
                setToggleQuestionModal({
                  show: false,
                  templateData: {
                    _id: "",
                    type: "",
                    status: "",
                    questionGroupedByCategory: "",
                    name: "",
                    resources: [],
                    category: [],
                    activeCategoryCount: 0,
                    activeQuestionCount: 0,
                  },
                })
              }
              templateData={toggleQuestionModal.templateData}
            />
          )}
        </>
      )}

      {isAddTemplateModalVisible && (
        <AddTemplateModal
          onBack={handleToggleAddTemplateModal}
          fetchAllTemplate={getTemplateData}
          templateId={templateId}
          setTemplateId={handleEditTemplate}
          editTemplate={EditTemplateData}
           
        />
      )}
    </Content>
  );
};

export default RTMTemplate;
